// streakLogic.js — pure fire-condition logic, importable by tests without Firebase deps.
//
// CFG-STREAK-WEB-1: the cadence is NOT defined here. Every value comes from the
// `streak` block of the `/config` payload (`config.json` → `/config` endpoint):
//   streak.milestones            e.g. [25, 50]  — exact vote counts that fire
//   streak.repeatEveryVotes      e.g. 100       — fires again on every multiple past the last milestone
//   streak.inviteSuppressionDays e.g. 30        — invite-block cooldown
// No default, no fallback, no local copy (IMPLEMENTATION_GUIDELINES.MD → Global config).
//
// D-1: when config has not resolved (or its fetch failed) the interrupt does not
// fire — identical to the sign-in wall, which already gates on config presence.
import { STORAGE_KEYS } from './v2Api';

/**
 * Invite-block cooldown. `streakConfig.inviteSuppressionDays` is required.
 * Without config we cannot tell whether the cooldown expired, so we treat it as
 * still active (suppress) rather than nag — that is a no-config *behaviour*,
 * not a hardcoded day count.
 */
export function inviteCooldownActive(streakConfig) {
  const raw = localStorage.getItem(STORAGE_KEYS.streakLastInviteAt);
  if (!raw) return false;
  const days = streakConfig?.inviteSuppressionDays;
  if (typeof days !== 'number') return true;
  const last = new Date(raw);
  const daysSince = (Date.now() - last.getTime()) / (1000 * 60 * 60 * 24);
  return daysSince < days;
}

/**
 * Pure schedule, entirely config-driven: fires on any count listed in
 * `streak.milestones`, and thereafter on every `streak.repeatEveryVotes`
 * multiple beyond the largest milestone. Used by VotingComponent.
 * Returns false when config is absent (D-1).
 */
export function streakScheduleFires(count, streakConfig) {
  const milestones = streakConfig?.milestones;
  if (!Array.isArray(milestones)) return false;
  if (milestones.includes(count)) return true;
  const repeat = streakConfig?.repeatEveryVotes;
  if (typeof repeat !== 'number' || repeat <= 0) return false;
  const lastMilestone = milestones.length ? Math.max(...milestones) : 0;
  return count > lastMilestone && count % repeat === 0;
}

/**
 * Drop the last emoji from a streak string (the "previous event" undo).
 *
 * FAV-STAR-1 / F14: this MUST slice by code point, not by UTF-16 code unit.
 * `slice(0, -2)` worked only because every vote emoji happened to be a surrogate
 * pair; `⭐` (U+2B50) is a single BMP code unit, so the old form chopped two —
 * orphaning the trailing half of the *preceding* emoji into localStorage.
 * The mutation check for this is: reimplement as `s.slice(0, -2)` → tests go red.
 */
export function removeLastVoteEmoji(streak) {
  if (!streak) return '';
  return [...streak].slice(0, -1).join('');
}

/** Full condition — fires only when schedule AND at least one block has content. */
export function streakInterruptFires(count, { isSignedIn, inGroupWithOthers }, streakConfig) {
  const showSignIn = !isSignedIn;
  const showInvite = !inGroupWithOthers && !inviteCooldownActive(streakConfig);
  if (!showSignIn && !showInvite) return false;
  return streakScheduleFires(count, streakConfig);
}
